import { useMemo, useState } from "react";
import { setPromoCodeProducts } from "../../api/mintSpace";
import { useTranslation } from "../../context/TranslationContext";
import { money } from "../../utils/promoCodes";
import Avatar from "../ds/Avatar";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import { STATE_LAYER } from "../ds/IconButton";
import InlineMessage from "../ds/InlineMessage";
import Modal from "../ds/Modal";
import SearchField from "../ds/SearchField";
import Switch from "../ds/Switch";

/**
 * Which products a promo code works on. Shown straight after a code is made, and again
 * whenever the owner opens it from the list — so a product created after the code can
 * be added to it. "All products" covers products added later by itself.
 */
export default function PromoCodeProductsPicker({ isDark, mintSpaceId, spaceName, promo, products, isNew, onBack, onSaved }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(Boolean(promo.appliesToAll));
  const [picked, setPicked] = useState(() => new Set(promo.products || []));
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Paid products first: they're the ones a discount means anything on.
  const ordered = useMemo(
    () => [...products].sort((a, b) => Number(a.isFree) - Number(b.isFree)),
    [products]
  );
  const paidCount = products.filter((p) => !p.isFree).length;

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (!all && picked.size === 0) {
      setError(t("promoCodes.products.pickOne"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await setPromoCodeProducts(mintSpaceId, promo.id, {
        appliesToAll: all,
        productIds: [...picked],
      });
      onSaved?.(res.data);
    } catch (err) {
      setError(err?.message || t("promoCodes.products.failed"));
    } finally {
      setBusy(false);
    }
  };

  const q = query.trim().toLowerCase();
  const shown = ordered.filter((p) => !q || (p.name || "").toLowerCase().includes(q));

  return (
    <Modal
      open
      onClose={onBack}
      title={t("promoCodes.products.modalTitle")}
      description={t("promoCodes.products.modalSubtitle", { code: promo.code })}
      closeLabel={t("common.close")}
      isDark={isDark}
    >
      <div className="flex flex-col gap-space-16">
        <SearchField value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("promoCodes.products.search")} />

        <div className="flex items-center justify-between gap-space-8">
          <div className="flex min-w-0 flex-col gap-space-4">
            <p className="text-body text-label-primary">{t("promoCodes.products.worksOnAll")}</p>
            <p className="text-footnote text-label-tertiary">{t("promoCodes.products.worksOnAllHint")}</p>
          </div>
          <Switch on={all} label={t("promoCodes.products.worksOnAll")} onClick={() => setAll(!all)} />
        </div>

        {!all && (
          <>
            <div className="h-px bg-separator-default" />
            {products.length === 0 ? (
              <p className="text-center text-subheadline text-label-secondary">{t("promoCodes.products.none")}</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-space-8">
                  <p className="min-w-0 truncate text-caps-label uppercase text-label-tertiary">
                    {t("promoCodes.products.productsIn", { space: spaceName })}
                  </p>
                  {paidCount > 1 && (
                    <Button
                      size="s"
                      variant="plain"
                      onClick={() =>
                        setPicked(
                          picked.size >= paidCount ? new Set() : new Set(products.filter((p) => !p.isFree).map((p) => p.id))
                        )
                      }
                    >
                      {picked.size >= paidCount ? t("promoCodes.products.clearAll") : t("promoCodes.products.selectAll")}
                    </Button>
                  )}
                </div>
                <div className="flex flex-col gap-space-8">
                  {shown.map((p) => {
                    const on = picked.has(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        aria-pressed={on}
                        disabled={p.isFree && !on}
                        onClick={() => toggle(p.id)}
                        className={`relative flex items-center gap-space-12 rounded-radius-md border p-space-12 text-left disabled:cursor-not-allowed disabled:opacity-40 ${
                          on ? "border-accent-default bg-fill-quaternary" : "border-separator-default"
                        } ${STATE_LAYER}`}
                      >
                        <Avatar size="l" src={p.image} name={p.name} />
                        <span className="flex min-w-0 flex-1 flex-col gap-space-4">
                          <span className="truncate text-subheadline-semibold text-label-primary">
                            {p.name || t("purchases.untitled")}
                          </span>
                          <span className="text-footnote text-label-secondary">
                            {p.isFree
                              ? t("promoCodes.products.free")
                              : `${money(p.price, p.currency)} · ${t(
                                  p.billingType === "recurring" ? "promoCodes.products.recurring" : "promoCodes.products.oneTime"
                                )}`}
                          </span>
                        </span>
                        <span
                          className={`grid size-[22px] shrink-0 place-items-center rounded-full text-accent-on ${
                            on ? "bg-accent-default" : "border border-separator-default"
                          }`}
                        >
                          {on && <Icon name="check" size={14} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-footnote text-label-tertiary">{t("promoCodes.products.pickHint")}</p>
              </>
            )}
          </>
        )}

        {error && <InlineMessage tone="danger">{error}</InlineMessage>}
      </div>

      <div className="flex justify-end gap-space-12">
        <Button variant="gray" onClick={onBack}>
          {isNew ? t("promoCodes.products.later") : t("common.cancel")}
        </Button>
        <Button loading={busy} onClick={save}>
          {t("promoCodes.products.save")}
        </Button>
      </div>
    </Modal>
  );
}
